import useTranslation from '../useTranslation';
import { BoundaryPartRequestBoundaryType } from 'generatedTypes/flight/models/boundaryPartRequestBoundaryType';
import { getGetTelemetryPointsUrl } from 'generatedTypes/flight/services/telemetry-point-controller';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { hectaresToAcres, squareMetersToAcres } from 'utils/areaUtil';
import { litersToGallons } from 'utils/volumeUtil';

import { DEFAULT_CENTER, DEFAULT_ZOOM, MAP_STYLE } from 'components/Map/constants';

import useApplication from 'hooks/useApplication';
import useApplicationMix from 'hooks/useApplicationMix';
import useBoundary from 'hooks/useBoundary';
import useFetch from 'hooks/useFetch';
import useFlightSimulation from 'hooks/useFlightSimulation';
import { DEFAULT_MISSION_CONFIG } from 'hooks/useFlightSimulation/constants';
import useFlights from 'hooks/useFlights';
import { useFloatingResourceAddContext } from 'hooks/useFloatingResourceAddContext';
import useJohnDeere from 'hooks/useJohnDeere';
import { useNavigationCollapse } from 'hooks/useNavigationCollapse';
import usePageRoutes from 'hooks/usePageRoutes';

import {
    DETAIL_TYPE_BOUNDARY,
    DETAIL_TYPE_CHEMICAL,
    DETAIL_TYPE_CLIENT,
    DETAIL_TYPE_ESTIMATED_ACRES,
    DETAIL_TYPE_FARM,
    DETAIL_TYPE_FIELD,
    DETAIL_TYPE_TOTAL_AREA_APPLIED,
    DETAIL_TYPE_TOTAL_VOLUME_APPLIED,
    TELEMETRY_PAGE_SIZE,
    TIMELINE_PAGE_SIZE
} from 'pages/ApplicationDetailsPage/constants';

import type {
    ApiPrescriptionZone,
    ApiRateBounds,
    ApplicationMixAttachment,
    ApplicationResponse,
    BoundaryResponse,
    CreateBoundaryRequest,
    DetailsViewEntityLinkProps,
    DetailsViewEntityMenuItem,
    FlightResponse,
    Geometry,
    MapInteractionOptions,
    MapOptions,
    PagedResponseTelemetryPointResponse,
    PrescriptionChangeHandler,
    TelemetryPointResponse
} from './types';

const useData = () => {
    const { applicationId } = useParams();
    const { state } = useLocation();
    const {
        ACRES,
        CREATE_FLIGHT_PLAN,
        EDIT_APPLICATION,
        EDIT_FLIGHT_PLAN,
        EXPORT_TO_JOHN_DEERE,
        GALLONS,
        SEND_TO_DRONE_RESULT
    } = useTranslation();
    const { isConnected } = useJohnDeere();
    const {
        assignBoundaryToApplication,
        fetchApplication,
        isLoading: isApplicationLoading
    } = useApplication();
    const { createBoundary, fetchBoundary, isLoading: isBoundaryLoading } = useBoundary();
    const { fetch: fetchFlights, flights = [], isLoading: isFlightLoading } = useFlights();
    const { authenticatedGet } = useFetch();
    const [realTelemetryPointsByFlightId, setRealTelemetryPointsByFlightId] = useState<
        Record<string, TelemetryPointResponse[]>
    >({});
    const [plannerSessionId, setPlannerSessionId] = useState<string | null>(null);

    const {
        isSimulating,
        simulatedFlights,
        simulatedTelemetryPointsByFlightId,
        simulationState,
        triggerSimulation
    } = useFlightSimulation({
        missionConfig: DEFAULT_MISSION_CONFIG,
        sessionId: plannerSessionId
    });

    const handlePlannerSessionIdChange = useCallback(
        (sessionId: string | null) => setPlannerSessionId(sessionId),
        []
    );

    const sendToDroneResultText = useMemo(() => {
        if (simulationState.status !== 'complete') {
            return null;
        }

        const { summary } = simulationState;

        return SEND_TO_DRONE_RESULT({
            flightSeconds: summary.flightDurationSeconds.toFixed(1),
            tankUsed: summary.tankUsedLitres.toFixed(1),
            waypointCount: summary.waypointCount
        });
    }, [SEND_TO_DRONE_RESULT, simulationState]);

    const { fetchApplicationMix } = useApplicationMix();

    const {
        applicationsPage,
        batteryDetailsPage,
        boundaryDetailsPage,
        clientDetailsPage,
        droneDetailsPage,
        farmDetailsPage,
        fieldDetailsPage,
        userDetailsPage
    } = usePageRoutes();
    const navigate = useNavigate();
    const [currentApplication, setCurrentApplication] = useState<ApplicationResponse | undefined>(
        undefined
    );

    const [isEditingMap, setIsEditingMap] = useState(state?.isEditingMap === true);
    const [isPlanning, setIsPlanning] = useState(state?.autoOpenPlanner === true);
    const [plannedFlightPaths, setPlannedFlightPaths] = useState<FlightResponse[]>([]);
    const [prescriptionZones, setPrescriptionZones] = useState<ApiPrescriptionZone[] | null>(null);
    const [prescriptionBounds, setPrescriptionBounds] = useState<ApiRateBounds | null>(null);
    const [droneMenuItems, setDroneMenuItems] = useState<DetailsViewEntityMenuItem[]>([]);
    const [batteryMenuItems, setBatteryMenuItems] = useState<DetailsViewEntityMenuItem[]>([]);
    const [scheduledPilotMenuItems, setScheduledPilotMenuItems] = useState<
        DetailsViewEntityMenuItem[]
    >([]);
    const [pilotMenuItems, setPilotMenuItems] = useState<DetailsViewEntityMenuItem[]>([]);
    const [boundary, setBoundary] = useState<BoundaryResponse | undefined>(undefined);
    const [attachedMix, setAttachedMix] = useState<ApplicationMixAttachment | undefined>(undefined);
    const [hasMoreFlights, setHasMoreFlights] = useState(false);
    const [selectedFlight, setSelectedFlight] = useState<FlightResponse | undefined>(undefined);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);

    useFloatingResourceAddContext(false);
    useNavigationCollapse(isEditingMap);

    const handleEditMapClick = () => {
        setIsEditingMap(true);
    };

    const handleEditMapClose = () => {
        setIsEditingMap(false);
    };
    const [isActivityOpen, setIsActivityOpen] = useState(false);
    const [isLinkedEntitiesOpen, setIsLinkedEntitiesOpen] = useState(false);
    const [isEditingForm, setIsEditingForm] = useState(false);
    const [isClosing, setIsClosing] = useState(false);

    const estimatedAcresEntityName = !boundary?.areaSqM
        ? ''
        : `${squareMetersToAcres(boundary.areaSqM)} ${ACRES}`;

    const totalVolumeAppliedEntityName = !currentApplication?.totalVolumeAppliedLiters
        ? ''
        : `${litersToGallons(currentApplication.totalVolumeAppliedLiters)} ${GALLONS}`;

    const totalAreaAppliedEntityName = !currentApplication?.totalAreaAppliedHectares
        ? ''
        : `${hectaresToAcres(currentApplication.totalAreaAppliedHectares)} ${ACRES}`;

    const entitiesLink: DetailsViewEntityLinkProps[] = [
        {
            entityName: currentApplication?.client?.name ?? '',
            entityRoute: clientDetailsPage(`${currentApplication?.client?.id}`),
            entityType: DETAIL_TYPE_CLIENT
        },

        {
            entityName: currentApplication?.parcel?.name ?? '',
            entityRoute: farmDetailsPage(`${currentApplication?.parcel?.id}`),
            entityType: DETAIL_TYPE_FARM
        },
        {
            entityName: currentApplication?.site?.name ?? '',
            entityRoute: fieldDetailsPage(`${currentApplication?.site?.id}`),
            entityType: DETAIL_TYPE_FIELD
        },
        {
            entityName: boundary?.name ?? '',
            entityRoute: boundary?.id ? boundaryDetailsPage(boundary.id) : undefined,
            entityType: DETAIL_TYPE_BOUNDARY
        },
        {
            entityName: estimatedAcresEntityName,
            entityRoute: undefined,
            entityType: DETAIL_TYPE_ESTIMATED_ACRES
        },
        {
            entityName: totalVolumeAppliedEntityName,
            entityRoute: undefined,
            entityType: DETAIL_TYPE_TOTAL_VOLUME_APPLIED
        },
        {
            entityName: totalAreaAppliedEntityName,
            entityRoute: undefined,
            entityType: DETAIL_TYPE_TOTAL_AREA_APPLIED
        }
    ];

    if (attachedMix?.mix && applicationId) {
        entitiesLink.push({
            entityName: attachedMix.mix.name,
            entityRoute: `/chemicals/mix-apply/app/${applicationId}`,
            entityType: DETAIL_TYPE_CHEMICAL
        });
    }

    useEffect(() => {
        if (!currentApplication) {
            return;
        }

        const getDetailsMenuItems = async () => {
            if (currentApplication.drones) {
                setDroneMenuItems(
                    currentApplication.drones.map(drone => ({
                        entityId: drone?.id ?? '',
                        entityName: drone?.name ?? '',
                        entityRoute: droneDetailsPage(`${drone?.id}`)
                    }))
                );
            } else {
                setDroneMenuItems([]);
            }

            if (currentApplication.batteries) {
                setBatteryMenuItems(
                    currentApplication.batteries.map(battery => ({
                        entityId: battery?.id ?? '',
                        entityName: battery?.name ?? '',
                        entityRoute: batteryDetailsPage(`${battery?.id}`)
                    }))
                );
            } else {
                setBatteryMenuItems([]);
            }

            if (currentApplication.scheduledPilots) {
                setScheduledPilotMenuItems(
                    currentApplication.scheduledPilots.map(pilot => ({
                        entityId: pilot?.id ?? '',
                        entityName: `${pilot?.firstName ?? ''} ${pilot?.lastName ?? ''}`.trim(),
                        entityRoute: userDetailsPage(`${pilot?.id}`)
                    }))
                );
            } else {
                setScheduledPilotMenuItems([]);
            }

            if (currentApplication.pilots) {
                setPilotMenuItems(
                    currentApplication.pilots.map(pilot => ({
                        entityId: pilot?.id ?? '',
                        entityName: `${pilot?.firstName ?? ''} ${pilot?.lastName ?? ''}`.trim(),
                        entityRoute: userDetailsPage(`${pilot?.id}`)
                    }))
                );
            } else {
                setPilotMenuItems([]);
            }
        };

        getDetailsMenuItems();

        const getBoundary = async () => {
            const boundary = await fetchBoundary({ id: currentApplication.boundaryId ?? '' });

            setBoundary(boundary ?? undefined);
        };

        if (currentApplication.boundaryId) {
            getBoundary();
        }

        const getAttachedMix = async () => {
            const attachment = await fetchApplicationMix({ applicationId: currentApplication.id });

            setAttachedMix(attachment ?? undefined);
        };

        if (currentApplication.id) {
            getAttachedMix();
        }

        const getFlights = async () => {
            const flightsGenerator = fetchFlights({
                applicationId,
                pageSize: TIMELINE_PAGE_SIZE,
                sort: [{ direction: 'ASC', field: 'startTime' }]
            });

            const { done } = await flightsGenerator.next();

            setHasMoreFlights(!done);
        };

        if (currentApplication.id) {
            getFlights();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentApplication]);

    useEffect(() => {
        let cancelled = false;

        const fetchAllTelemetryPoints = async (
            flightId: string
        ): Promise<TelemetryPointResponse[] | null> => {
            const accumulated: TelemetryPointResponse[] = [];
            let pageToken: string | undefined;

            do {
                const url = getGetTelemetryPointsUrl(flightId, {
                    pageSize: TELEMETRY_PAGE_SIZE,
                    pageToken
                });
                const { data, error } =
                    await authenticatedGet<PagedResponseTelemetryPointResponse>(url);

                if (error || !data) {
                    console.error(`Telemetry fetch failed for flight ${flightId}:`, error);

                    return null;
                }

                accumulated.push(...(data.objects ?? []));
                pageToken = data.nextPageToken ?? undefined;
            } while (pageToken);

            accumulated.sort(
                (a, b) => new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime()
            );

            return accumulated;
        };

        const loadTelemetry = async () => {
            const entries = await Promise.all(
                flights.map(async flight => {
                    const points = await fetchAllTelemetryPoints(flight.id);

                    if (points === null) {
                        return null;
                    }

                    return [flight.id, points] as const;
                })
            );

            if (cancelled) {
                return;
            }

            const validEntries = entries.filter(
                (entry): entry is readonly [string, TelemetryPointResponse[]] => entry !== null
            );

            setRealTelemetryPointsByFlightId(Object.fromEntries(validEntries));
        };

        if (flights.length > 0) {
            loadTelemetry();
        }

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flights]);

    const geometryParts = useMemo(
        () =>
            boundary?.boundaryParts?.map(part => ({
                ...part,
                applicationName:
                    part.boundaryType === BoundaryPartRequestBoundaryType.APPLY
                        ? currentApplication?.name
                        : undefined
            })) ?? [],
        [boundary, currentApplication?.name]
    );

    const geometry: Geometry = useMemo(
        () => ({
            isEditable: isEditingMap,
            parts: geometryParts
        }),
        [geometryParts, isEditingMap]
    );

    const mapOptions: MapOptions = {
        accessToken: import.meta.env.VITE_MAPBOX_ACCESS_TOKEN as string,
        center: DEFAULT_CENTER,
        mapStyle: MAP_STYLE,
        zoom: DEFAULT_ZOOM
    };

    const mapInteractionOptions: MapInteractionOptions = {
        hasDrawingControls: isEditingMap,
        hasLayerToggles: true,
        hasMapControls: true
    };

    const handleMapComplete = async (result: CreateBoundaryRequest) => {
        if (!applicationId || !currentApplication?.name || !result) {
            setIsEditingMap(false);

            return;
        }

        const boundaryName = `${currentApplication.name} - ${result.name}`;
        const createdBoundary = await createBoundary({
            boundary: { boundaryParts: result.boundaryParts, name: boundaryName }
        });

        if (!createdBoundary) {
            setIsEditingMap(false);

            return;
        }

        await assignBoundaryToApplication({
            applicationId,
            boundaryId: createdBoundary.id
        });

        setBoundary(createdBoundary);
        setIsEditingMap(false);
    };

    const getCurrentApplication = async () => {
        if (applicationId) {
            const application = await fetchApplication({ id: applicationId });

            setCurrentApplication(application ?? undefined);
        }
    };

    useEffect(() => {
        if (applicationId) {
            getCurrentApplication();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicationId]);

    const handleNextFlightsPage = async () => {
        const { done } = await fetchFlights({
            applicationId,
            pageSize: TIMELINE_PAGE_SIZE,
            sort: [{ direction: 'ASC', field: 'startTime' }]
        }).next();

        setHasMoreFlights(!done);
    };

    const handleEditClick = () => {
        setIsEditingForm(true);
    };

    const handleToggleActivity = () => {
        setIsActivityOpen(previous => !previous);
    };

    const handleToggleLinkedEntities = () => {
        setIsLinkedEntitiesOpen(previous => !previous);
    };

    const handleEditClose = () => {
        if (isEditingForm) {
            setIsEditingForm(false);
            setIsClosing(false);
            getCurrentApplication();
        }
    };

    const hasExistingFlightPlan =
        (currentApplication?.applicationSettings?.waypoints?.length ?? 0) > 0;

    const flightPlanMenuText = hasExistingFlightPlan ? EDIT_FLIGHT_PLAN : CREATE_FLIGHT_PLAN;

    const handlePathPlanClick = () => {
        if (!currentApplication?.boundaryId) {
            return;
        }

        setIsPlanning(true);
    };

    const handlePathPlanClose = () => {
        setIsPlanning(false);
        setPlannedFlightPaths([]);
    };

    const handlePathPlanSaved = ({ application }: { application: ApplicationResponse }) => {
        setCurrentApplication(application);
        setIsPlanning(false);
        setPlannedFlightPaths([]);
    };

    const handlePlannedFlightPathsChange = ({
        flightPaths: planned
    }: {
        flightPaths: FlightResponse[];
    }) => {
        setPlannedFlightPaths(planned);
    };

    const handlePrescriptionChange: PrescriptionChangeHandler = useCallback(({ bounds, zones }) => {
        setPrescriptionZones(zones);
        setPrescriptionBounds(bounds);
    }, []);

    // Real flights win when present. With no real flights, the layer
    // group falls through to whatever simulated trace is in state (or an
    // empty array if the user has not triggered Send to Drone yet). The
    // ToggleFlightPathsButton toggles visibility of FLIGHT_PATH_LAYERS
    // regardless, so "path layer button toggles simulated ONLY IF no real
    // flights returned" falls out of this branch naturally.
    const hasRealFlights = flights.length > 0;

    const mapFlightPaths = isPlanning
        ? plannedFlightPaths
        : hasRealFlights
          ? flights
          : simulatedFlights;

    const telemetryPointsByFlightId = useMemo(
        () => (hasRealFlights ? realTelemetryPointsByFlightId : simulatedTelemetryPointsByFlightId),
        [hasRealFlights, realTelemetryPointsByFlightId, simulatedTelemetryPointsByFlightId]
    );

    const handleExportToJohnDeereClick = () => {
        setIsExportModalOpen(true);
    };

    const handleExportModalClose = () => {
        setIsExportModalOpen(false);
    };

    const handleOpenMixApplyClick = () => {
        if (applicationId) {
            navigate(`/chemicals/mix-apply/app/${applicationId}`);
        }
    };

    const dropdownMenuItems = [
        {
            onClick: handleEditClick,
            text: EDIT_APPLICATION
        },
        {
            isDisabled: !currentApplication?.boundaryId,
            onClick: handlePathPlanClick,
            text: flightPlanMenuText
        },
        {
            isDisabled: !attachedMix?.mix,
            onClick: handleOpenMixApplyClick,
            text: 'Open Mix & Apply'
        },
        {
            hasJohnDeereIcon: true,
            isDisabled: !isConnected || !currentApplication?.boundaryId,
            onClick: handleExportToJohnDeereClick,
            text: EXPORT_TO_JOHN_DEERE
        }
    ];

    const handleNavigateBack = () => {
        navigate(applicationsPage);
    };

    const handleClose = () => {
        if (isEditingForm) {
            setIsClosing(true);
        } else {
            handleNavigateBack();
        }
    };

    const handleSelectFlight = (flight: FlightResponse) => {
        setSelectedFlight(flight);
    };

    return {
        batteryMenuItems,
        boundary,
        currentApplication,
        droneMenuItems,
        dropdownMenuItems,
        entitiesLink,
        flights,
        geometry,
        handleClose,
        handleEditClose,
        handleEditMapClick,
        handleEditMapClose,
        handleExportModalClose,
        handleMapComplete,
        handleNextFlightsPage,
        handlePathPlanClose,
        handlePathPlanSaved,
        handlePlannedFlightPathsChange,
        handlePlannerSessionIdChange,
        handlePrescriptionChange,
        handleSelectFlight,
        handleSendToDrone: triggerSimulation,
        handleToggleActivity,
        handleToggleLinkedEntities,
        hasMoreFlights,
        isActivityOpen,
        isApplicationLoading,
        isBoundaryLoading,
        isClosing,
        isEditingForm,
        isEditingMap,
        isExportModalOpen,
        isFlightLoading,
        isLinkedEntitiesOpen,
        isPlanning,
        isSendingToDrone: isSimulating,
        mapFlightPaths,
        mapInteractionOptions,
        mapOptions,
        pilotMenuItems,
        prescriptionBounds,
        prescriptionZones,
        scheduledPilotMenuItems,
        selectedFlight,
        sendToDroneResultText,
        setIsClosing,
        telemetryPointsByFlightId
    };
};

export default useData;
